// feature/lifecycle-redesign.
// Lawyer-side action surface, used by AbogadoCaseDetail.js (replaces the
// Sprint 54 "Acciones del abogado" card with MarcarContestada +
// SolicitarModeracion). State-aware, three sub-modals via CenteredModal:
//   1. ContestarModal      — POST /api/cases/:id/marcar-contestada
//                            (auto-routes to pendiente_archivar Sprint 56)
//   2. EsperandoInsumoModal — POST /api/cases/:id/esperando-insumo
//                             (one-way for the lawyer; Camilo's upload
//                              releases + starts Timer 2)
//   3. ModeracionModal     — POST /api/cases/:id/solicitar-moderacion
//                            (Sprint 50 escalation route)
//
// Status banners: Esperando insumo, Timer 2 active, pendiente_archivar,
// archivado. Each branch self-gates so unauthorized users see nothing.
//
// "Contestar sin esperar insumo" is just the same Contestar modal —
// backend infers the escape path from the esperando_insumo lock state.

import React, { useState } from "react";
import CenteredModal from "../CenteredModal";
import Spinner from "../Spinner";
import LitisoftIncompleteModal from "../LitisoftIncompleteModal";
import { cases as casesApi, moderation as moderationApi } from "../../api/endpoints";
import { useToast } from "../Toast";
// feature/draft-persistence. Auto-save the modal's text
// fields so a crash / accidental close / reload doesn't lose typed
// reasoning. Drop-in replacement for useState for the persistable
// fields (categoria, notas, reason).
import useDraftField from "../../hooks/useDraftField";
import {
  canMarkContestadaDirectly,
  canRequestModeration,
  availableContestadaCategorias,
  CONTESTADA_LABELS,
} from "../../utils/permissions";

const fmt = (d) => d ? new Date(d).toLocaleString("es-CO") : "—";

export default function LawyerActionPanel({ caseData, user, onCaseUpdate }) {
  const [showContestar, setShowContestar]   = useState(false);
  const [showEsperando, setShowEsperando]   = useState(false);
  const [showModeracion, setShowModeracion] = useState(false);

  if (!caseData || !user) return null;

  const canContestar = canMarkContestadaDirectly(user, caseData);
  const canModerar   = canRequestModeration(user, caseData);

  const inEsperando        = caseData.esperando_insumo_at && !caseData.esperando_insumo_released_at;
  const timer2Active       = caseData.timer2_started_at && !caseData.timer2_ended_at;
  const isPendienteArchivar = caseData.estado === "pendiente_archivar";
  const isArchivado         = caseData.estado === "archivado";

  // Terminal banners — surface only, no actions left.
  if (isArchivado) {
    return (
      <div className="card tight" style={{ padding: 12, borderLeft: "3px solid var(--text-muted)", background: "rgba(107,114,128,0.08)" }}>
        <strong>Caso archivado</strong>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          Archivado el {fmt(caseData.archivado_at)}.
        </div>
      </div>
    );
  }
  if (isPendienteArchivar) {
    return (
      <div className="card tight" style={{ padding: 12, borderLeft: "3px solid var(--accent-mint)", background: "var(--color-success-bg)" }}>
        <strong>Contestada el {fmt(caseData.contestada_at)}</strong>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          Esperando que Reparto lo archive para cerrar el caso.
        </div>
      </div>
    );
  }

  // Esperando-button only when timer1 hasn't ended yet AND case is at lawyer.
  const showEsperandoButton =
    !inEsperando &&
    caseData.estado === "asignado_abogado" &&
    !caseData.timer1_ended_at;

  return (
    <div className="col" style={{ gap: 10 }}>
      {inEsperando && (
        <div className="card tight" style={{
          padding: 12, borderLeft: "3px solid var(--color-warning)",
          background: "var(--color-warning-bg)", color: "var(--color-warning-ink)",
        }}>
          <strong>⏰ Esperando insumo desde {fmt(caseData.esperando_insumo_at)}</strong>
          <div style={{ fontSize: 12, marginTop: 2 }}>
            Cuando reparto cargue la respuesta de Previsora, su plazo de
            <strong> 3 horas</strong> arranca automáticamente. Si decide
            contestar igual sin el insumo, use el botón de abajo.
          </div>
        </div>
      )}

      {timer2Active && (
        <div className="card tight" style={{
          padding: 12, borderLeft: "3px solid var(--color-danger)",
          background: "var(--color-danger-bg)", color: "var(--color-danger-ink)",
        }}>
          <strong>📩 Insumo recibido — plazo de 3h</strong>
          <div style={{ fontSize: 12, marginTop: 2 }}>
            Vence: {fmt(caseData.timer2_deadline_at)}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {canContestar && (
          <button onClick={() => setShowContestar(true)}>
            {inEsperando ? "Contestar sin esperar insumo" : "Marcar como contestada"}
          </button>
        )}
        {showEsperandoButton && (
          <button className="secondary" onClick={() => setShowEsperando(true)}>
            ⏰ Esperando insumo
          </button>
        )}
        {canModerar && (
          <button className="ghost sm" onClick={() => setShowModeracion(true)}>
            Solicitar revisión de moderación
          </button>
        )}
      </div>

      <ContestarModal
        open={showContestar}
        onClose={() => setShowContestar(false)}
        caseData={caseData} user={user}
        onSuccess={() => { setShowContestar(false); onCaseUpdate?.(); }}
        isEscape={!!inEsperando}
      />
      <EsperandoModal
        open={showEsperando}
        onClose={() => setShowEsperando(false)}
        caseData={caseData}
        onSuccess={() => { setShowEsperando(false); onCaseUpdate?.(); }}
      />
      <ModeracionModal
        open={showModeracion}
        onClose={() => setShowModeracion(false)}
        caseData={caseData}
        onSuccess={() => { setShowModeracion(false); onCaseUpdate?.(); }}
      />
    </div>
  );
}

function ContestarModal({ open, onClose, caseData, user, onSuccess, isEscape }) {
  const toast = useToast();
  // Sprint 62b — drafts persisted per case_id so a reload mid-typing
  // doesn't blow the lawyer's reasoning away.
  const draftKey = caseData?.id ? `contestar-${caseData.id}` : null;
  const [categoria, setCategoria, clearCategoria] = useDraftField(`${draftKey}-categoria`, "");
  const [notas,     setNotas,     clearNotas]     = useDraftField(`${draftKey}-notas`,     "");
  const [busy, setBusy] = useState(false);
  // sprint83 — Litisoft gate. Backend returns 400 with
  //   { error: "litisoft_incomplete", missing_fields: [...] }
  // when the case is gated and missing fields. We open a secondary
  // modal listing the missing labels.
  const [litisoftMissing, setLitisoftMissing] = useState(null);

  const cats = availableContestadaCategorias(user);

  async function submit() {
    if (!categoria) return;
    setBusy(true);
    try {
      await casesApi.marcarContestada(caseData.id, { categoria, notas: notas.trim() || null });
      toast.push("Caso marcado como contestada. Pasa a Reparto para archivar.");
      // Sprint 62b — clear persisted draft on success.
      clearCategoria(); clearNotas();
      onSuccess?.();
    } catch (e) {
      const data = e?.response?.data;
      if (data?.error === "litisoft_incomplete" && Array.isArray(data.missing_fields)) {
        setLitisoftMissing(data.missing_fields);
      } else {
        toast.push(data?.error || "Error al marcar contestada", "error");
      }
    } finally { setBusy(false); }
  }

  return (
    <CenteredModal
      open={open} onClose={busy ? undefined : onClose}
      title={isEscape ? "Contestar sin esperar insumo" : "Marcar como contestada"}
      subtitle={`Caso ${caseData?.case_number || ""}. El caso pasa a Reparto para archivar.`}
      size="md"
      footer={<>
        <button className="secondary" onClick={onClose} disabled={busy}>Cancelar</button>
        <button onClick={submit} disabled={busy || !categoria}>
          {busy && <Spinner color="currentColor" />} Confirmar
        </button>
      </>}
    >
      {isEscape && (
        <div className="card tight" style={{
          padding: 10, marginBottom: 12,
          borderLeft: "3px solid var(--color-warning)",
          background: "var(--color-warning-bg)", fontSize: 12,
        }}>
          Está saliendo del estado <strong>Esperando insumo</strong>. El caso pasa a
          archivar igual; su plazo no se va a reactivar.
        </div>
      )}
      <label style={{ display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13, marginBottom: 12 }}>
        Categoría <span style={{ color: "var(--color-danger)" }}>*</span>
        <select value={categoria} onChange={(e) => setCategoria(e.target.value)}
                disabled={busy} required autoFocus
                style={{ marginTop: 4, width: "100%" }}>
          <option value="">— Seleccionar —</option>
          {cats.map((c) => (
            <option key={c} value={c}>{CONTESTADA_LABELS[c] || c}</option>
          ))}
        </select>
      </label>
      <label style={{ display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
        Notas (opcional, máx 500 caracteres)
        <textarea value={notas} onChange={(e) => setNotas(e.target.value)}
                  rows={3} maxLength={500} disabled={busy}
                  placeholder="Detalles adicionales sobre la respuesta…"
                  style={{ marginTop: 4, width: "100%" }} />
      </label>
      <LitisoftIncompleteModal
        open={!!litisoftMissing}
        missingFields={litisoftMissing || []}
        onClose={() => setLitisoftMissing(null)}
        onGoToPanel={() => {
          setLitisoftMissing(null);
          onClose?.();
        }}
      />
    </CenteredModal>
  );
}

function EsperandoModal({ open, onClose, caseData, onSuccess }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await casesApi.esperandoInsumo(caseData.id);
      toast.push("Caso marcado como Esperando insumo. Su reloj se detuvo.");
      onSuccess?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al marcar Esperando insumo", "error");
    } finally { setBusy(false); }
  }

  return (
    <CenteredModal
      open={open} onClose={busy ? undefined : onClose}
      title="¿Marcar este caso como Esperando insumo?"
      subtitle={`Caso ${caseData?.case_number || ""}.`}
      size="sm"
      footer={<>
        <button className="secondary" onClick={onClose} disabled={busy}>Cancelar</button>
        <button onClick={submit} disabled={busy}>
          {busy && <Spinner color="currentColor" />} Confirmar
        </button>
      </>}
    >
      <p style={{ fontSize: 13, margin: "0 0 12px" }}>
        Su reloj se va a detener para este caso. Cuando reparto cargue la
        respuesta de Previsora, su nuevo plazo de <strong>3 horas</strong>
        arranca automáticamente.
      </p>
      <div className="card tight" style={{
        padding: 10, borderLeft: "3px solid var(--color-warning)",
        background: "var(--color-warning-bg)", fontSize: 12,
      }}>
        <strong>No se puede deshacer.</strong> Si después decide contestar
        sin esperar el insumo, tendrá un botón aparte para hacerlo. Si
        necesita que reparto haga algo urgente, use <em>Solicitar revisión
        de moderación</em>.
      </div>
    </CenteredModal>
  );
}

function ModeracionModal({ open, onClose, caseData, onSuccess }) {
  const toast = useToast();
  // Sprint 62b — moderación reason often runs long (≥20 chars required,
  // typically 100+); high cost if lost. Persisted per case.
  const draftKey = caseData?.id ? `moderacion-reason-${caseData.id}` : null;
  const [reason, setReason, clearReason] = useDraftField(draftKey, "");
  const [busy, setBusy] = useState(false);
  const ok = reason.trim().length >= 20;

  async function submit() {
    if (!ok) return;
    setBusy(true);
    try {
      await moderationApi.request(caseData.id, reason.trim());
      toast.push("Solicitud de moderación enviada.");
      clearReason();
      onSuccess?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al solicitar moderación", "error");
    } finally { setBusy(false); }
  }

  return (
    <CenteredModal
      open={open} onClose={busy ? undefined : onClose}
      title="Solicitar revisión de moderación"
      subtitle={`Caso ${caseData?.case_number || ""}. Reparto / dirección lo revisarán.`}
      size="md"
      footer={<>
        <button className="secondary" onClick={onClose} disabled={busy}>Cancelar</button>
        <button onClick={submit} disabled={busy || !ok}>
          {busy && <Spinner color="currentColor" />} Enviar
        </button>
      </>}
    >
      <p style={{ fontSize: 13, margin: "0 0 8px" }}>Moderación puede:</p>
      <ul style={{ fontSize: 13, paddingLeft: 18, marginTop: 0 }}>
        <li>Extender el plazo</li>
        <li>Marcar el insumo deficiente para que avance</li>
        <li>Reasignar el caso a otro abogado</li>
        <li>Confirmar la vencida</li>
      </ul>
      <label style={{ display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13, marginTop: 10 }}>
        Razón <span style={{ color: "var(--color-danger)" }}>*</span>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                  rows={4} maxLength={1000} disabled={busy} autoFocus
                  placeholder="Mínimo 20 caracteres explicando el problema."
                  style={{ marginTop: 4, width: "100%" }} />
        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
          {reason.trim().length} / 20 caracteres mínimos
        </div>
      </label>
    </CenteredModal>
  );
}
